// Convertir una imagen al formato que pide un destino externo — ADR-2026-09-14-configuracion-de-descarga.
//
// El caso que lo trajo: una tienda pidió las fotos en 1000×1500, WebP, entre 150 y
// 200 KB. Nada de eso se puede pedir directamente:
//
//   · Las MEDIDAS no coinciden con lo que genera la app (4:5) y estirar deforma al
//     producto, así que se RECORTA al centro. `planDeRecorte` decide qué pedazo del
//     original sobrevive; es puro y es lo único que hay que mirar para entender qué
//     se pierde.
//   · El PESO no es un parámetro: `canvas.toBlob` toma una calidad, no un tamaño. Hay
//     que encodar, medir y volver a probar. `encodarBajoElTope` hace esa búsqueda con
//     un encoder inyectado, así que se testea sin canvas.
//
// El tope de peso es un TECHO, no una banda: se arranca por la mejor calidad y, si ya
// entra, se entrega ahí mismo. Pesar menos de lo pedido no es un defecto — nadie
// rechaza una foto por liviana—, y engordarla a propósito sería gastar bytes en nada.

/** Medidas de una imagen, en píxeles. */
export interface Medidas {
  ancho: number;
  alto: number;
}

/** El pedazo del original que entra en el destino: los `s*` de `drawImage`. */
export interface Recorte {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/** Peor calidad aceptable: más abajo el JPEG/WebP empieza a mostrar bloques. */
export const CALIDAD_MIN = 0.55;
/** Mejor calidad que se prueba. Arriba de esto el peso sube sin que se note. */
export const CALIDAD_MAX = 0.92;
/** Cuántas veces, como mucho, se encoda la misma imagen buscando el peso. */
export const INTENTOS_MAX = 5;

/**
 * Qué pedazo del original llena el destino sin deformarlo ("cover", centrado).
 *
 * Se recorta el lado que sobra: un 4:5 hacia un 2:3 —que es más angosto— pierde los
 * COSTADOS, y un 9:16 hacia el mismo 2:3 pierde arriba y abajo. El rectángulo que sale
 * siempre tiene el aspecto del destino, que es lo que garantiza que nada se estire.
 */
export function planDeRecorte(origen: Medidas, destino: Medidas): Recorte {
  const aspectoDestino = destino.ancho / destino.alto;
  const aspectoOrigen = origen.ancho / origen.alto;
  if (aspectoOrigen > aspectoDestino) {
    // El origen es más ancho: sobra ancho.
    const sw = Math.round(origen.alto * aspectoDestino);
    return { sx: Math.round((origen.ancho - sw) / 2), sy: 0, sw, sh: origen.alto };
  }
  if (aspectoOrigen < aspectoDestino) {
    const sh = Math.round(origen.ancho / aspectoDestino);
    return { sx: 0, sy: Math.round((origen.alto - sh) / 2), sw: origen.ancho, sh };
  }
  return { sx: 0, sy: 0, sw: origen.ancho, sh: origen.alto };
}

/**
 * Encoda buscando la mejor calidad que entre bajo `topeBytes`.
 *
 * El encoder se inyecta: acá adentro no hay canvas, así que la búsqueda —que es la
 * parte que se puede equivocar— se prueba con aritmética.
 *
 * Tres encodes fijos antes de cualquier búsqueda fina, porque los dos extremos
 * contestan casi todo:
 *
 *   1. `CALIDAD_MAX`. Si entra, se devuelve ahí mismo: no existe nada mejor y el caso
 *      común (1000×1500 WebP pesa ~150 KB) sale en un solo encode.
 *   2. `CALIDAD_MIN`. Si ni así entra, no hay nada que buscar: se entrega igual. Una
 *      imagen 30 KB más pesada de lo pedido sirve; no entregar nada, no.
 *   3. Recién ahí, bisección entre la que entró y la que no.
 */
export async function encodarBajoElTope<T>(
  encodar: (calidad: number) => Promise<{ salida: T; bytes: number }>,
  topeBytes: number,
): Promise<T> {
  const alta = await encodar(CALIDAD_MAX);
  if (alta.bytes <= topeBytes) return alta.salida;

  const baja = await encodar(CALIDAD_MIN);
  if (baja.bytes > topeBytes) return baja.salida;

  // `lo` entra, `hi` no. La respuesta está en el medio.
  let lo = CALIDAD_MIN;
  let hi = CALIDAD_MAX;
  let mejor = baja.salida;
  for (let i = 2; i < INTENTOS_MAX; i += 1) {
    const q = (lo + hi) / 2;
    const r = await encodar(q);
    if (r.bytes <= topeBytes) {
      mejor = r.salida;
      lo = q;
    } else {
      hi = q;
    }
  }
  return mejor;
}

/**
 * La proporción de unas medidas, en su forma más simple ("1000×1500" → "2:3"), o `null`
 * si no simplifica a números que alguien reconozca.
 *
 * Es para MOSTRAR: le dice al usuario contra qué se va a recortar lo que genere. Un
 * "1001:1500" no le dice nada a nadie, así que en ese caso no se muestra nada.
 */
export function proporcionDe(ancho: number, alto: number): string | null {
  const mcd = (a: number, b: number): number => (b === 0 ? a : mcd(b, a % b));
  const d = mcd(Math.round(ancho), Math.round(alto));
  if (d === 0) return null;
  const a = Math.round(ancho) / d;
  const b = Math.round(alto) / d;
  // Arriba de 20 ya no es una proporción que se lea: es el par de números original.
  return a <= 20 && b <= 20 ? `${a}:${b}` : null;
}

// ── La cáscara: del blob al blob ────────────────────────────────────────────
// Todo lo de acá arriba es aritmética probada; lo de acá abajo es canvas, que sólo
// existe en el navegador. La regla es la misma que en `aligerarImagen`: ante CUALQUIER
// falla se devuelve el original. Esto convierte, nunca bloquea una descarga.

/** Los formatos que el canvas del navegador sabe escribir. */
export type FormatoDeSalida = 'webp' | 'jpg' | 'png';

const MIME: Record<FormatoDeSalida, string> = {
  webp: 'image/webp',
  jpg: 'image/jpeg',
  png: 'image/png',
};

export interface PerfilDeSalida {
  ancho: number;
  alto: number;
  formato: FormatoDeSalida;
  /** Techo de peso en KB. `null` = sin techo: un solo encode en la mejor calidad. */
  pesoMaxKB: number | null;
}

const leer = (blob: Blob): Promise<{ img: HTMLImageElement; url: string }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('no se pudo leer la imagen'));
    };
    img.src = url;
  });

/**
 * La imagen recortada al centro, en las medidas del perfil y bajo su techo de peso.
 *
 * El PNG ignora el parámetro de calidad —es sin pérdida—, así que no tiene sentido
 * buscarle el peso: se encoda una vez y se entrega. Quien necesita un peso concreto
 * pide WebP o JPEG.
 */
export async function convertirImagen(blob: Blob, perfil: PerfilDeSalida): Promise<Blob> {
  let leida: { img: HTMLImageElement; url: string };
  try {
    leida = await leer(blob);
  } catch {
    return blob;
  }
  const { img, url } = leida;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = perfil.ancho;
    canvas.height = perfil.alto;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    // JPEG no tiene alfa: un PNG con transparencia saldría sobre negro.
    if (perfil.formato === 'jpg') {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    const { sx, sy, sw, sh } = planDeRecorte(
      { ancho: img.naturalWidth, alto: img.naturalHeight },
      { ancho: perfil.ancho, alto: perfil.alto },
    );
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

    const mime = MIME[perfil.formato];
    const encodar = async (calidad: number) => {
      const salida = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, mime, calidad),
      );
      return { salida, bytes: salida?.size ?? Number.POSITIVE_INFINITY };
    };

    const sinTecho = perfil.pesoMaxKB === null || perfil.formato === 'png';
    const salida = sinTecho
      ? (await encodar(CALIDAD_MAX)).salida
      : await encodarBajoElTope(encodar, perfil.pesoMaxKB! * 1024);
    return salida ?? blob;
  } catch {
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}
